import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import PropertyCard from "../components/PropertyCard";
import { useAuth } from "../context/AuthContext";
import { getProperties } from "../services/api";

export default function Listings() {
  const location = useLocation();
  const { role } = useAuth();

  const [city, setCity] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [properties, setProperties] = useState([]);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const pageSize = 6;

  const normalizeText = useCallback((value) => String(value || "").trim().toLowerCase(), []);

  useEffect(() => {
    const load = async () => {
      try {
        setError("");
        const statusParam = role === "admin" && statusFilter ? statusFilter : undefined;
        const data = await getProperties(statusParam);
        setProperties(Array.isArray(data) ? data : []);
      } catch {
        setError("Could not load properties.");
        setProperties([]);
      }
    };

    load();
  }, [role, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [city, propertyType, statusFilter]);

  const filteredProperties = useMemo(() => {
    const filtered = properties.filter((item) => {
      const itemCity = normalizeText(item.city || item.City);
      const itemType = normalizeText(item.type || item.PropertyType || "house");
      const cityMatches = !city || itemCity === normalizeText(city);
      const typeMatches = !propertyType || itemType === normalizeText(propertyType);

      return cityMatches && typeMatches;
    });

    return filtered.sort((a, b) => {
      const aNum = Number(a.id);
      const bNum = Number(b.id);
      const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum);

      if (bothNumeric) {
        return bNum - aNum;
      }

      return String(b.id || "").localeCompare(String(a.id || ""), undefined, { numeric: true });
    });
  }, [city, normalizeText, properties, propertyType]);

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / pageSize));
  const visibleProperties = filteredProperties.slice((page - 1) * pageSize, page * pageSize);
  const hasResults = visibleProperties.length > 0;

  return (
    <div className="container page">
      <div className="page-header">
        <div>
          <h2 className="section-title">Property Listings</h2>
          <p className="page-subtitle">
            {role === "admin"
              ? "Admin view includes moderation statuses."
              : "Browse published properties only."}
          </p>
        </div>
      </div>

      {location.state?.message ? (
        <div className="feedback-banner success-banner">{location.state.message}</div>
      ) : null}

      {error ? <div className="feedback-banner error-banner">{error}</div> : null}

      <div className="filter-panel">
        <select className="form-input" onChange={(event) => setCity(event.target.value)} value={city}>
          <option value="">All Cities</option>
          <option value="srinagar">Srinagar</option>
          <option value="gulmarg">Gulmarg</option>
          <option value="anantnag">Anantnag</option>
          <option value="shopian">Shopian</option>
        </select>

        <select
          className="form-input"
          onChange={(event) => setPropertyType(event.target.value)}
          value={propertyType}
        >
          <option value="">All Types</option>
          <option value="house">House</option>
          <option value="land">Land</option>
          <option value="shop">Shop</option>
        </select>

        {role === "admin" ? (
          <select
            className="form-input"
            onChange={(event) => setStatusFilter(event.target.value)}
            value={statusFilter}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="published">Published</option>
            <option value="rejected">Rejected</option>
          </select>
        ) : null}
      </div>

      {hasResults ? (
        <>
          <div className="grid property-grid">
            {visibleProperties.map((property) => (
              <PropertyCard key={property.id} p={property} />
            ))}
          </div>

          <div className="pagination-bar">
            <button
              className="btn btn-secondary"
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </button>
            <span className="pagination-label">
              Page {page} of {totalPages}
            </span>
            <button
              className="btn btn-secondary"
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </button>
          </div>
        </>
      ) : (
        <div className="listing-empty-state">
          <h3>No listings match these filters.</h3>
          <p>Try another city or property type to broaden the results.</p>
        </div>
      )}
    </div>
  );
}
